import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSeo, useJsonLd } from "@/lib/api";
import { site } from "@/config/site";
import { Section, Reveal, Eyebrow } from "@/components/common/Section";
import FaqAccordion from "@/components/common/FaqAccordion";
import FinalCta from "@/components/common/FinalCta";
import Hero from "@/components/home/Hero";
import Orienteur from "@/components/home/Orienteur";
import Difference from "@/components/home/Difference";
import { publishedArticles } from "@/data/articles";

export default function Home() {
  const { t, to, lang } = useLang();

  useSeo({
    lang,
    path: "",
    title: "NEKASSUR — Courtier en assurances",
    description:
      "NEKASSUR, courtier en assurances indépendant. Des solutions adaptées aux particuliers comme aux entreprises, y compris pour les situations qui sortent des critères habituels.",
  });

  useJsonLd("ld-home", {
    "@context": "https://schema.org",
    "@type": "InsuranceAgency",
    name: "NEKASSUR",
    url: "https://nekassur.fr",
    logo: "https://nekassur.fr/brand/nekassur-logo-officiel.png",
    image: "https://nekassur.fr/brand/nekassur-logo-officiel.png",
    description: t.footer.tagline,
    areaServed: "FR",
    address: { "@type": "PostalAddress", addressLocality: site.city, addressCountry: "FR" },
    founder: { "@type": "Person", name: site.founder },
  });

  const recent = publishedArticles().slice(0, 3);

  return (
    <>
      <Hero />
      <Orienteur />
      <Difference />

      {/* ===================== METHODE ===================== */}
      <Section tone="light">
        <Reveal className="max-w-2xl">
          <h2 className="nek-h2 whitespace-pre-line">{t.method.title}</h2>
        </Reveal>
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {t.method.steps.map((step) => (
            <Reveal key={step.n} className="nek-card p-6">
              <span className="font-heading text-3xl font-extrabold text-nek-blue/30">{step.n}</span>
              <h3 className="mt-3 text-lg font-heading font-bold text-nek-navy">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-nek-gray">{step.text}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ===================== FRONTALIERS TEASER ===================== */}
      <Section tone="white">
        <Reveal className="grid items-center gap-10 rounded-[24px] border border-nek-border bg-nek-light p-8 sm:p-12 lg:grid-cols-2">
          <div>
            <Eyebrow>{t.frontaliersTeaser.eyebrow}</Eyebrow>
            <h2 className="mt-4 text-2xl font-heading font-extrabold text-nek-navy sm:text-3xl">{t.frontaliersTeaser.title}</h2>
            <p className="mt-5 leading-relaxed text-nek-gray">{t.frontaliersTeaser.text}</p>
            <Link to={to(t.nav.frontaliers.path)} data-testid="frontaliers-teaser-cta" className="btn-navy mt-7">
              {t.frontaliersTeaser.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-nek-border shadow-soft">
            <img src="https://images.unsplash.com/photo-1531973576160-7125cd663d86" alt="Professionnel frontalier accompagné par NEKASSUR" className="h-72 w-full object-cover" loading="lazy" />
          </div>
        </Reveal>
      </Section>

      {/* ===================== FONDATEUR (éditorial) ===================== */}
      <Section tone="light">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center lg:gap-16">
          <Reveal>
            <Eyebrow>{t.founder.eyebrow}</Eyebrow>
            <blockquote className="mt-5 font-heading text-3xl font-bold leading-tight text-nek-navy sm:text-4xl lg:text-[2.75rem]">
              {t.founder.quote}
            </blockquote>
            <div className="mt-8 flex items-center gap-4">
              <div className="h-px w-16 bg-nek-teal" />
              <div>
                <p className="font-heading text-base font-bold text-nek-navy">Yanis Nekhla</p>
                <p className="text-sm text-nek-gray">{t.founder.role}</p>
              </div>
            </div>
          </Reveal>
          <Reveal className="lg:border-l lg:border-nek-border lg:pl-16">
            <h2 className="text-xl font-heading font-bold text-nek-navy">{t.founder.title}</h2>
            <p className="mt-4 leading-relaxed text-nek-gray">{t.founder.text}</p>
            <div className="mt-6">
              <Link to={to("/a-propos")} data-testid="founder-cta" className="link-arrow">{t.founder.cta} <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* ===================== PARTENAIRES ===================== */}
      <Section tone="white">
        <Reveal className="max-w-2xl">
          <Eyebrow>{t.partners.eyebrow}</Eyebrow>
          <h2 className="nek-h2 mt-4">{t.partners.title}</h2>
          <p className="nek-intro mt-4">{t.partners.intro}</p>
        </Reveal>
        <div className="mt-10 rounded-[20px] bg-nek-navy p-8 text-center sm:p-12">
          <p className="font-heading text-5xl font-extrabold text-nek-teal-100 sm:text-6xl">{site.partnerNetwork.count}</p>
          <p className="mt-2 font-heading text-lg font-bold text-white">{t.partners.networkLabel}</p>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-white/70">{t.partners.note}</p>
        </div>
      </Section>

      {/* ===================== CONSEILS ===================== */}
      <Section tone="white">
        <Reveal className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-xl">
            <Eyebrow>{t.conseils.eyebrow}</Eyebrow>
            <h2 className="nek-h2 mt-4">{t.conseils.title}</h2>
          </div>
          <Link to={to("/conseils")} className="link-arrow">{t.cta.allArticles} <ArrowRight className="h-4 w-4" /></Link>
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {recent.map((a) => (
            <Link key={a.slug} to={to(`/conseils/${a.slug}`)} data-testid={`home-article-${a.slug}`} className="nek-card group overflow-hidden hover:-translate-y-1 hover:shadow-lift">
              <div className="overflow-hidden">
                <img src={a.image} alt={a[lang].title} className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              </div>
              <div className="p-5">
                <span className="text-xs font-semibold uppercase tracking-wider text-nek-blue">{t.conseils.categories[a.category]}</span>
                <h3 className="mt-2 text-base font-heading font-bold text-nek-navy">{a[lang].title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-nek-gray">{a[lang].excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* ===================== FAQ ===================== */}
      <Section tone="light" id="faq">
        <Reveal className="mx-auto max-w-2xl text-center">
          <Eyebrow className="justify-center">{t.faq.eyebrow}</Eyebrow>
          <h2 className="nek-h2 mt-4">{t.faq.title}</h2>
        </Reveal>
        <div className="mt-10">
          <FaqAccordion items={t.faq.items.slice(0, 6)} />
        </div>
      </Section>

      <FinalCta />
    </>
  );
}
